import {
  EMPTY_STRING,
  RETURN_URL_QUERY_PARAM,
} from '@assetron/common/constants/constant';

/**
 * Simple utility for query parameter handling during navigation
 */
export class QueryParamPersistence {
  /**
   * Get current query parameters from the browser URL
   */
  static getCurrentQueryParams(): string {
    return window.location.search;
  }

  /**
   * Append current query parameters to a path
   */
  static appendCurrentQueryParams(basePath: string): string {
    const currentParams = this.getCurrentQueryParams();

    if (!currentParams || currentParams === '?') {
      return basePath;
    }

    // Remove leading '?' if present
    const cleanParams = currentParams.startsWith('?')
      ? currentParams.slice(1)
      : currentParams;

    if (!cleanParams) {
      return basePath;
    }

    // Parse existing parameters in basePath
    const [pathWithoutQuery, existingQuery] = basePath.split('?');
    const existingParams = new URLSearchParams(existingQuery || EMPTY_STRING);
    const currentParamsObj = new URLSearchParams(cleanParams);

    // Merge parameters, with current params taking precedence
    currentParamsObj.forEach((value, key) => {
      existingParams.set(key, value);
    });

    const finalParams = existingParams.toString();
    return finalParams
      ? `${pathWithoutQuery}?${finalParams}`
      : pathWithoutQuery;
  }

  /**
   * Preserve current query parameters during navigation
   */
  static preserveQueryParamsInNavigation(targetPath: string): string {
    return this.appendCurrentQueryParams(targetPath);
  }

  /**
   * Append specific query parameters to a path
   */
  static appendQueryParamsToPath(
    basePath: string,
    queryParams: string,
  ): string {
    if (!queryParams || queryParams === '?') {
      return basePath;
    }

    // Remove leading '?' if present
    const cleanParams = queryParams.startsWith('?')
      ? queryParams.slice(1)
      : queryParams;

    if (!cleanParams) {
      return basePath;
    }

    // Parse existing parameters in basePath
    const [pathWithoutQuery, existingQuery] = basePath.split('?');
    const existingParams = new URLSearchParams(existingQuery || EMPTY_STRING);
    const newParams = new URLSearchParams(cleanParams);

    // Merge parameters, with new params taking precedence
    newParams.forEach((value, key) => {
      existingParams.set(key, value);
    });

    const finalParams = existingParams.toString();
    return finalParams
      ? `${pathWithoutQuery}?${finalParams}`
      : pathWithoutQuery;
  }

  static hasPersistedQueryParams(): boolean {
    return !!this.getCurrentQueryParams();
  }

  /**
   * Add query parameters to a path for navigation within auth app
   */
  static addPersistedParamsToPath(path: string): string {
    return this.appendCurrentQueryParams(path);
  }

  /**
   * Append query parameters to a URL for compatibility
   */
  static appendPersistedQueryParams(baseUrl: string): string {
    return this.appendCurrentQueryParams(baseUrl);
  }

  /**
   * Get return URL with current query parameters
   */
  static getReturnUrlWithQueryParams(defaultUrl: string): string {
    const currentParams = this.getCurrentQueryParams();

    if (!currentParams) {
      return defaultUrl;
    }

    const params = new URLSearchParams(
      currentParams.startsWith('?') ? currentParams.slice(1) : currentParams,
    );

    // Avoid nesting returnUrl inside the destination URL during auth redirect.
    params.delete(RETURN_URL_QUERY_PARAM);

    const persistedParams = params.toString();
    if (!persistedParams) {
      return defaultUrl;
    }

    return this.appendQueryParamsToPath(defaultUrl, persistedParams);
  }

  /**
   * Create a Navigate component props object with preserved query parameters
   */
  static createNavigateWithParams(targetPath: string) {
    const pathWithParams = this.preserveQueryParamsInNavigation(targetPath);
    return { to: pathWithParams, replace: true };
  }
}
